// WebCrypto implementation of the react-native-aes-crypto API surface the app
// uses: randomKey, pbkdf2, encrypt, decrypt, hmac256, sha256.
//
// Encoding conventions match the native module and the Node reference in
// src/services/encryption/__mocks__/aes-crypto-node.ts exactly:
//   - keys, IVs, salts-as-keys, digests: lowercase hex
//   - ciphertext: base64
//   - plaintext, HMAC input, PBKDF2 password and salt: UTF-8 strings
//   - pbkdf2 `length` is in BITS
//
// Byte-for-byte parity with mobile is verified by the Phase 1 interop suite;
// nothing in the app's encryption logic changes for web.

const subtle = globalThis.crypto.subtle;
const utf8 = new TextEncoder();
const utf8dec = new TextDecoder();

function toHex(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < view.length; i += 1) out += view[i].toString(16).padStart(2, '0');
  return out;
}

function fromHex(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('aes-crypto: hex string has odd length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new Error('aes-crypto: invalid hex');
    out[i] = byte;
  }
  return out;
}

// Chunked: a document envelope is tens of megabytes, and one call per byte
// takes seconds at that size. 32K code units per call stays under every
// engine's argument limit.
const CHUNK = 0x8000;

function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const parts: string[] = [];
  for (let i = 0; i < view.length; i += CHUNK) {
    parts.push(String.fromCharCode.apply(null, view.subarray(i, i + CHUNK) as unknown as number[]));
  }
  return btoa(parts.join(''));
}

function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

const HASHES: Record<string, string> = {
  sha1: 'SHA-1',
  sha256: 'SHA-256',
  sha512: 'SHA-512',
};

export async function randomKey(length: number): Promise<string> {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return toHex(bytes);
}

export async function pbkdf2(
  password: string,
  salt: string,
  cost: number,
  length: number,
  algorithm: 'sha1' | 'sha256' | 'sha512' = 'sha256',
): Promise<string> {
  const hash = HASHES[algorithm];
  if (!hash) throw new Error(`aes-crypto: unsupported pbkdf2 hash ${algorithm}`);
  const baseKey = await subtle.importKey('raw', utf8.encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'PBKDF2', hash, salt: utf8.encode(salt), iterations: cost },
    baseKey,
    length,
  );
  return toHex(bits);
}

async function importAesKey(keyHex: string, usage: KeyUsage): Promise<CryptoKey> {
  return subtle.importKey('raw', fromHex(keyHex), { name: 'AES-CBC' }, false, [usage]);
}

export async function encrypt(
  text: string,
  keyHex: string,
  ivHex: string,
  algorithm: string = 'aes-256-cbc',
): Promise<string> {
  if (algorithm !== 'aes-256-cbc') {
    throw new Error(`aes-crypto: unsupported algorithm ${algorithm}`);
  }
  const key = await importAesKey(keyHex, 'encrypt');
  const out = await subtle.encrypt({ name: 'AES-CBC', iv: fromHex(ivHex) }, key, utf8.encode(text));
  return toBase64(out);
}

export async function decrypt(
  ciphertextB64: string,
  keyHex: string,
  ivHex: string,
  algorithm: string = 'aes-256-cbc',
): Promise<string> {
  if (algorithm !== 'aes-256-cbc') {
    throw new Error(`aes-crypto: unsupported algorithm ${algorithm}`);
  }
  const key = await importAesKey(keyHex, 'decrypt');
  const out = await subtle.decrypt(
    { name: 'AES-CBC', iv: fromHex(ivHex) },
    key,
    fromBase64(ciphertextB64),
  );
  return utf8dec.decode(out);
}

export async function hmac256(text: string, keyHex: string): Promise<string> {
  const key = await subtle.importKey(
    'raw',
    fromHex(keyHex),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await subtle.sign('HMAC', key, utf8.encode(text));
  return toHex(sig);
}

export async function sha256(text: string): Promise<string> {
  return toHex(await subtle.digest('SHA-256', utf8.encode(text)));
}

export async function sha1(text: string): Promise<string> {
  return toHex(await subtle.digest('SHA-1', utf8.encode(text)));
}

export async function sha512(text: string): Promise<string> {
  return toHex(await subtle.digest('SHA-512', utf8.encode(text)));
}

export default { randomKey, pbkdf2, encrypt, decrypt, hmac256, sha256, sha1, sha512 };
